import type { AuthUser } from './auth-provider'

// Tenant ids vary per environment, so they are NOT hardcoded — they come from
// build-time Vite env vars (set per `.env.<mode>`). No fallback: if a var is
// missing the corresponding gate is neutral (a single id is `null` → matches no
// tenant; a list is `[]` → matches no tenant). Set the vars in each environment.

function parseTenantId(value: string | undefined): number | null {
  if (!value) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function parseTenantIds(value: string | undefined): readonly number[] {
  if (!value) return []
  return value
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n))
}

/** Paypaga client tenant (sandbox banner + mock-data variant). */
export const PAYPAGA_TENANT_ID = parseTenantId(import.meta.env.VITE_PAYPAGA_TENANT_ID)

/** Infra tenant (infra-only nav items and reports). */
export const INFRA_TENANT_ID = parseTenantId(import.meta.env.VITE_INFRA_TENANT_ID)

/** Grupo Pereyra client tenant (Epicor-specific payment blotter behavior). */
export const GRUPO_PEREYRA_TENANT_ID = parseTenantId(import.meta.env.VITE_GRUPO_PEREYRA_TENANT_ID)

/** Ecobank demo tenant (Anexo 8: GL Posting Alerts screen + ISO 20022 messages). */
export const ECOBANK_TENANT_ID = parseTenantId(import.meta.env.VITE_ECOBANK_TENANT_ID)

/**
 * Tenants allowed to see the REAL reconciliation module (the screens wired to
 * Reconciliation.Api). Every other tenant sees the legacy mock/"upgrade"
 * screens. Configured via VITE_RECONCILIATION_REAL_TENANT_IDS (comma-separated).
 */
export const RECONCILIATION_REAL_TENANT_IDS = parseTenantIds(
  import.meta.env.VITE_RECONCILIATION_REAL_TENANT_IDS,
)

/**
 * Tenants that see the datalake dashboards POC (UC-100), read from the datalake
 * reporting API. Configured via VITE_DATALAKE_DASHBOARDS_TENANT_IDS
 * (comma-separated); in production only TH Demo (12).
 */
export const DATALAKE_DASHBOARDS_TENANT_IDS = parseTenantIds(
  import.meta.env.VITE_DATALAKE_DASHBOARDS_TENANT_IDS,
)

export function getUserTenantId(user: AuthUser | null | undefined): number | null {
  return user?.entityId ?? null
}

export function isPaypagaTenant(user: AuthUser | null | undefined): boolean {
  const id = getUserTenantId(user)
  return id !== null && PAYPAGA_TENANT_ID !== null && id === PAYPAGA_TENANT_ID
}

export function isInfraTenant(user: AuthUser | null | undefined): boolean {
  const id = getUserTenantId(user)
  return id !== null && INFRA_TENANT_ID !== null && id === INFRA_TENANT_ID
}

export function canSeeRealReconciliation(user: AuthUser | null | undefined): boolean {
  const id = getUserTenantId(user)
  return id !== null && RECONCILIATION_REAL_TENANT_IDS.includes(id)
}

export function canSeeDatalakeDashboards(user: AuthUser | null | undefined): boolean {
  const id = getUserTenantId(user)
  return id !== null && DATALAKE_DASHBOARDS_TENANT_IDS.includes(id)
}

export function isGrupoPereyraTenant(user: AuthUser | null | undefined): boolean {
  const id = getUserTenantId(user)
  return id !== null && GRUPO_PEREYRA_TENANT_ID !== null && id === GRUPO_PEREYRA_TENANT_ID
}

export function isEcobankTenant(user: AuthUser | null | undefined): boolean {
  const id = getUserTenantId(user)
  return id !== null && ECOBANK_TENANT_ID !== null && id === ECOBANK_TENANT_ID
}
